'use client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { signOut, useSession } from 'next-auth/react';
import {
  GET_CART_ENDPOINT,
  GET_DELETE_CART_ENDPOINT,
} from '../_libs/utils/constants/endpoints';

// item sample :***************************
// number:1
// price_id: 326
// variation:
// id: 326
// product: {
//   media:{
//     thumb:
//         {id: 49799, name: 'اکسس پوینت سیسکو مدل AIR-CAP3602E-E-K9', full_path: 'uploads/2023/04/اکسس-پوینت-سیسکو-مدل-AIR-CAP3602E-E-K9.jpeg', alt: null}}
//   slug: "cisco-access-point-model-air-cap3602e-e-k9"
//   title: "اکسس پوینت سیسکو مدل AIR-CAP3602E-E-K9"
// product_id: 49793
// regular: 23000
// sale: 22000
/**
 * ------------------------------------------------------------------------------------------------
 * Add To cart On Server
 * ------------------------------------------------------------------------------------------------
 */
const addToCartOnServer = async (items, accessToken) => {
  var formData = new FormData();
  if (items.length > 0) {
    items.forEach((item, index) => {
      formData.append(`items[${index}][id]`, item.price_id);
      formData.append(`items[${index}][number]`, item.number);
    });
  }

  const response = await fetch(GET_CART_ENDPOINT, {
    method: 'POST',
    body: formData,
    headers: {
      Authorization: 'Bearer ' + accessToken,
      Accept: 'application/json',
    },
  });

  return response.json();
};
/**
 * ------------------------------------------------------------------------------------------------
 * Remove cart item From server
 * ------------------------------------------------------------------------------------------------
 */
const removeFromCartOnServer = async (
  productId,
  number = 1,
  giftId = null,
  accessToken
) => {
  const response = await fetch(GET_DELETE_CART_ENDPOINT, {
    method: 'POST',
    body: JSON.stringify({
      item: productId,
      number: number,
      gift_price_id: giftId,
    }),
    headers: {
      Authorization: 'Bearer ' + accessToken,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to remove item from cart');
  }

  return response.json();
};
/**
 * ------------------------------------------------------------------------------------------------
 * Get cart From server
 * ------------------------------------------------------------------------------------------------
 */
const getCartFromServer = async (accessToken) => {
  const response = await fetch(GET_CART_ENDPOINT, {
    method: 'GET',
    headers: {
      Authorization: 'Bearer ' + accessToken,
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    if (response.status == 401) {
      signOut();
    }
    throw new Error('Failed to get cart items');
  }

  try {
    return await response.json();
  } catch (error) {
    console.error('Failed to parse JSON response:', error);
    return []; // Provide an empty object as a default value
  }
};
/**
 * ------------------------------------------------------------------------------------------------
 * Get cart From Storage
 * ------------------------------------------------------------------------------------------------
 */
const getCartFromLocalStorage = () => {
  let cart = { items: [] };
  if (typeof localStorage !== 'undefined') {
    const storedCart = JSON.parse(localStorage.getItem('cart'));
    if (storedCart && Array.isArray(storedCart.items)) {
      cart = storedCart;
    } else {
      localStorage.removeItem('cart');
      localStorage.setItem('cart', JSON.stringify({ items: [] }));
    }
  }

  return cart;
};
/**
 * ------------------------------------------------------------------------------------------------
 * Add to cart in LocalSotrage
 * ------------------------------------------------------------------------------------------------
 */
const addToCartInLocalStorage = (products) => {
  if (typeof localStorage !== 'undefined') {
    const existingCart = getCartFromLocalStorage();

    products.forEach((product) => {
      const existingCartItem = existingCart.items.find(
        (item) => item.price_id === product.price_id
      );

      if (existingCartItem) {
        existingCartItem.number += product.number;
      } else {
        existingCart.items.push({ ...product });
      }
    });

    localStorage.setItem('cart', JSON.stringify(existingCart));
  }
};
/**
 * ------------------------------------------------------------------------------------------------
 * Remove From Cart in LocalSotrage
 * ------------------------------------------------------------------------------------------------
 */
const removeFromCartInLocalStorage = (productId, number = 1) => {
  if (typeof localStorage !== 'undefined') {
    const existingCart = getCartFromLocalStorage();

    const existingCartItem = existingCart.items.find(
      (item) => item.price_id === productId
    );

    if (existingCartItem) {
      if (existingCartItem.number === number || existingCartItem.number === 1) {
        existingCart.items = existingCart.items.filter(
          (item) => item.price_id !== productId
        );
        localStorage.setItem('cart', JSON.stringify(existingCart));
      } else {
        existingCartItem.number -= number;
        localStorage.setItem('cart', JSON.stringify(existingCart));
      }
    }
  }
};
/**
 * ------------------------------------------------------------------------------------------------
 * reset localStorage Cart
 * ------------------------------------------------------------------------------------------------
 */
const removeAllFromLocalStorage = () => {
  localStorage.setItem('cart', JSON.stringify({ items: [] }));
};
/**
 * ***************************************************************************************
 * Hooks
 * ***************************************************************************************
 */

const useCart = () => {
  const { data: session, status } = useSession();

  const queryClient = useQueryClient();
  const accessToken = session?.user?.accessToken;

  const addToCartMutation = useMutation({
    mutationFn: (items) => addToCartOnServer(items, accessToken),
    onSuccess: () => {
      queryClient.invalidateQueries('cart');
    },
  });

  const removeFromCartMutation = useMutation({
    mutationFn: ({ productId, number, giftId }) =>
      removeFromCartOnServer(productId, number, giftId, accessToken),
    onSuccess: () => {
      queryClient.invalidateQueries('cart');
    },
  });

  const getCartQuery = useQuery({
    queryKey: ['cart'],
    queryFn: () =>
      accessToken ? getCartFromServer(accessToken) : getCartFromLocalStorage(),

    enabled: status === 'authenticated' || status === 'unauthenticated',
    manual: true,
  });

  const addToCart = async ({ items }) => {
    if (session?.user) {
      try {
        const response = await addToCartMutation.mutateAsync(items);
        if (response.status !== 200) {
          toast.error(response.message);
        } else {
          return response;
        }
      } catch (error) {
        console.error('Failed to add item to cart on server:', error);
        toast.error('محصول به سبد خرید شما اضافه نشد ');
        throw error;
      }
    } else {
      addToCartInLocalStorage(items);
      queryClient.setQueryData('cart', getCartFromLocalStorage());
      queryClient.invalidateQueries('cart');
    }
  };

  const removeFromCart = async (productId, number) => {
    if (session?.user) {
      removeFromCartMutation.mutate({ productId, number });
    } else {
      removeFromCartInLocalStorage(productId, number);
      queryClient.setQueryData('cart', getCartFromLocalStorage());
    }
  };

  const emptyCart = () => {
    removeAllFromLocalStorage();
    queryClient.setQueryData('cart', getCartFromLocalStorage());
  };

  return {
    addToCart,
    removeFromCart,
    emptyCart,
    cart: getCartQuery.data,
    cartLoading: getCartQuery.isLoading,
    cartError: getCartQuery.error,
  };
};

export default useCart;
